import { useApi } from "@/lib/api/useApi";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { COLLECTIONS, ETHOS_IMAGE } from "@/lib/api/mock-data";
import { collectionsQuery, newArrivalsQuery, settingsQuery, heroSlidesQuery } from "@/lib/api/queries";
import { ContactSection } from "@/components/layout/ContactSection";
import { Reveal } from "@/components/motion/Reveal";
import { Icon } from "@/components/store/Icon";
import { TypedText } from "@/components/store/TypedText";
import { ProductCard } from "@/components/store/ProductCard";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { useLocalizedContent } from "@/lib/localize";
import { imgUrl } from "@/lib/format";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import "swiper/css/free-mode";


const getCollectionLink = (href: string) => {
  if (!href) return "/shop";
  if (href.startsWith('/')) return href;
  if (href.includes('=')) return href.startsWith('?') ? `/shop${href}` : `/shop?${href}`;
  return `/shop?category=${href}`;
};
import { readItems } from "@/lib/api/normalize";

/** Hero slider cadence (ms). */
const HERO_SLIDE_INTERVAL_MS = 2000;

function HomePage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const { collectionTitle, ctaLabel } = useLocalizedContent();
  const q1 = newArrivalsQuery();
  const q2 = collectionsQuery();
  const q3 = settingsQuery();
  const q4 = heroSlidesQuery();
  const { data: arrivals } = useApi(q1.queryKey, q1.queryFn);
  const { data: collections } = useApi(q2.queryKey, q2.queryFn);
  const { data: settingsRes } = useApi(q3.queryKey, q3.queryFn);
  const { data: slidesRes } = useApi(q4.queryKey, q4.queryFn);

  const settings = (settingsRes as any)?.settings ?? settingsRes ?? {};
  // The API may answer with a bare array or `{ items }`; `is_visible` /
  // `sort_order` are optional, so a missing flag counts as visible.
  const HERO_SLIDES: string[] = readItems<any>(slidesRes)
    .filter((s: any) => s?.is_visible !== false && s?.isVisible !== false)
    .sort((a: any, b: any) => (a?.sort_order ?? a?.sortOrder ?? 0) - (b?.sort_order ?? b?.sortOrder ?? 0))
    .map((s: any) => imgUrl(s?.image_url ?? s?.imageUrl))
    .filter(Boolean);

  const collectionsSwiper = useRef<SwiperClass | null>(null);
  const [slide, setSlide] = useState(0);
  const slideCount = Math.max(HERO_SLIDES.length, 1);
  const currentSlide = slide % slideCount;
  const nextSlide = (currentSlide + 1) % slideCount;
  const bigSrc = HERO_SLIDES[currentSlide];
  const smallSrc = HERO_SLIDES[nextSlide];
  const rtl = typeof document !== "undefined" && document.documentElement.dir === "rtl";
  const rtlSign = rtl ? -1 : 1;

  // Promote the small preview into the big frame on a fixed cadence.
  useEffect(() => {
    if (slideCount < 2) return;
    const id = window.setInterval(
      () => setSlide((s) => (s + 1) % slideCount),
      parseInt(settings.hero_slide_interval_ms) || HERO_SLIDE_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [slideCount, settings.hero_slide_interval_ms]);




  const [feature, ...stacked] = collections ?? [];


  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative w-full lg:min-h-[70vh] flex items-center overflow-hidden px-4 sm:px-8 lg:px-10 xl:px-14 py-6 md:py-8 lg:py-10 bg-surface-container-lowest">
        <div className="max-w-[1240px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 xl:gap-10 items-center relative">
          <div className="lg:col-span-6 xl:col-span-6 z-20 relative">
            <div className="space-y-5 md:space-y-6 animate-fade-in max-w-lg">
              <div className="space-y-3">
                <span className="text-xs font-semibold uppercase tracking-[0.4em] text-golden-latte block">
                  {(isAr ? settings.hero_tagline_ar : settings.hero_tagline_en) || t("hero.season")}
                </span>
                <h1 className="font-display-lg text-[clamp(36px,4.8vw,68px)] leading-[1.04] text-deep-chocolate italic font-medium tracking-tight">
                  {(isAr ? settings.hero_title_ar : settings.hero_title_en) || t("hero.titleA")} <br />
                  <TypedText
                    text={(isAr ? settings.hero_title2_ar : settings.hero_title2_en) || t("hero.titleB")}
                    className="not-italic font-bold ltr:ms-3 ltr:md:ms-6 inline-block"
                    cursorClassName="text-golden-latte"
                  />
                </h1>

                <p className="text-sm sm:text-base lg:text-[17px] text-on-surface-variant max-w-md mt-4 leading-relaxed font-normal">
                  {t("hero.subtitle")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-3">
                <Link
                  to="/shop"
                  className="px-7 sm:px-10 py-3.5 sm:py-4 bg-golden-latte text-white font-label-md text-xs sm:text-sm uppercase tracking-[0.2em] rounded-lg hover:brightness-110 transition-all luxury-shadow text-nowrap"
                >
                  {t("common.shopNow")}
                </Link>
                <Link
                  to="/shop"
                  className="group inline-flex items-center gap-2.5 font-label-md text-xs sm:text-sm uppercase tracking-widest text-deep-chocolate hover:text-golden-latte transition-all"
                >
                  {t("hero.viewCategories")}
                  <Icon name="arrow_forward" className="text-sm group-hover:translate-x-2 transition-transform rtl:rotate-180" />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 xl:col-span-6 relative w-full flex justify-center lg:justify-start ps-0 lg:ps-6 mt-4 lg:mt-0">
            {/* Big + small cards share a wrapper so the preview sits on the big card with a consistent gap */}
            <div className="relative w-[82%] sm:w-[85%] max-w-[320px] sm:max-w-[380px] lg:w-full lg:max-w-[490px] xl:max-w-[530px] aspect-square mx-auto lg:mx-0">
              <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl bg-surface-dim/30">
                <AnimatePresence mode="popLayout" initial={false}>
                  {bigSrc && (
                    <motion.img
                      key={`big-${bigSrc}`}
                      src={bigSrc}
                      alt=""
                      aria-hidden="true"
                      fetchPriority="high"
                      loading="eager"
                      initial={{ opacity: 0, scale: 1.08 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.02 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                </AnimatePresence>
              </div>

              {smallSrc && slideCount > 1 && (
                <button
                  type="button"
                  onClick={() => setSlide((s) => (s + 1) % slideCount)}
                  aria-label={t("hero.nextCollection")}
                  className="absolute -bottom-3 -start-3 sm:-bottom-4 sm:-start-4 lg:-bottom-6 lg:-start-6 w-20 h-20 sm:w-24 sm:h-24 lg:w-34 lg:h-34 z-10 rounded-xl overflow-hidden border-[3px] sm:border-4 lg:border-[5px] border-surface-container-lowest luxury-shadow transition-transform hover:-translate-y-2 duration-500 bg-surface-dim/30 aspect-square"
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.img
                      key={`small-${smallSrc}`}
                      src={smallSrc}
                      alt=""
                      aria-hidden="true"
                      initial={{ opacity: 0, x: `${rtlSign * -20}%`, y: "20%", scale: 0.96 }}
                      animate={{ opacity: 1, x: "0%", y: "0%", scale: 1 }}
                      exit={{ opacity: 0, x: `${rtlSign * 45}%`, y: "-40%", scale: 1.12 }}
                      transition={{ duration: 0.7, ease: "easeInOut" }}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </AnimatePresence>
                </button>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* Featured collections */}
      <Reveal as="section" className="py-section-gap-desktop px-grid-margin max-w-[1200px] mx-auto">
        <div className="flex flex-col gap-8 md:gap-12 ">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between border-b border-outline-variant/30 pb-6 sm:pb-8">
            <div className="space-y-2">
              <span className="font-label-sm text-label-sm text-golden-latte uppercase tracking-[0.3em]">
                {t("hero.selection")}
              </span>
              <h2 className="font-display-lg text-headline-lg text-deep-chocolate">{t("hero.collections")}</h2>
            </div>
            <Link
              to="/shop"
              className="font-label-md text-label-md uppercase tracking-widest text-deep-chocolate hover:text-golden-latte transition-colors flex items-center gap-2 group"
            >
              {t("common.explore")}
              <Icon name="east" className="text-sm group-hover:translate-x-1 transition-transform rtl:rotate-180" />
            </Link>
          </div>

          {/* Mobile: swipeable slider */}
          <div className="md:hidden relative">
            <Swiper
              modules={[FreeMode]}
              freeMode
              slidesPerView={1.5}
              spaceBetween={16}
              dir="ltr"
              onSwiper={(s) => (collectionsSwiper.current = s)}
              className="-mx-grid-margin !px-grid-margin"
            >
              {collections?.map((collection) => (
                <SwiperSlide key={collection.id} className="!h-auto">
                  <Link
                    to={getCollectionLink(collection.href)}
                    className="relative block w-full aspect-square overflow-hidden rounded-xl bg-surface-dim/30"
                  >
                    <img
                      src={imgUrl(collection.image) || undefined}
                      alt={collectionTitle(collection.title)}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-deep-chocolate/80 to-transparent" />
                    <div className="absolute bottom-6 inset-x-6 text-white">
                      <h3 className="font-headline-md text-headline-md mb-1">{collectionTitle(collection.title)}</h3>
                      <span className="font-label-sm text-label-sm uppercase tracking-widest">{ctaLabel(collection.cta)}</span>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                aria-label={t("hero.previousCollection")}
                onClick={() => collectionsSwiper.current?.slidePrev()}
                className="w-10 h-10 rounded-full border border-outline-variant flex items-center justify-center text-deep-chocolate"
              >
                <Icon name="west" className="text-[18px] rtl:rotate-180" />
              </button>
              <button
                type="button"
                aria-label={t("hero.nextCollection")}
                onClick={() => collectionsSwiper.current?.slideNext()}
                className="w-10 h-10 rounded-full border border-outline-variant flex items-center justify-center text-deep-chocolate"
              >
                <Icon name="east" className="text-[18px] rtl:rotate-180" />
              </button>
            </div>
          </div>


          {/* Tablet & desktop */}
          {feature && (
            <div className="hidden md:grid md:grid-cols-12 gap-6 lg:gap-8 ">
              {/* Left Column (6 of 12) */}
              <Link
                to={getCollectionLink(feature.href)}
                className="md:col-span-6 relative group overflow-hidden rounded-xl aspect-square block"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url("${imgUrl(feature.image)}")` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep-chocolate/70 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                <div className="absolute bottom-8 start-8 lg:bottom-12 lg:start-12 text-white space-y-3">
                  <h3 className="font-display-lg text-display-lg italic">{collectionTitle(feature.title)}</h3>
                  <span className="inline-block font-label-md text-label-md uppercase tracking-widest border-b border-white pb-1 group-hover:text-golden-latte group-hover:border-golden-latte transition-all">
                    {ctaLabel(feature.cta)}
                  </span>
                </div>
              </Link>

              {/* Right Column (6 of 12) containing 2x2 grid of smaller square cards */}
              <div className="md:col-span-6 grid grid-cols-2 gap-6 lg:gap-8">
                {stacked?.map((collection) => (
                  <Link
                    key={collection.id}
                    to={getCollectionLink(collection.href)}
                    className="relative group overflow-hidden rounded-xl bg-surface-dim/30 aspect-square block"
                  >
                    <img
                      src={imgUrl(collection.image) || undefined}
                      alt={collectionTitle(collection.title)}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
                      <h3 className="font-headline-md text-headline-md mb-2">{collectionTitle(collection.title)}</h3>
                      <span className="font-label-sm text-label-sm uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {ctaLabel(collection.cta)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </Reveal>

      {/* New arrivals */}
      <section className="bg-surface-container-low py-section-gap-desktop ">
        <div className="px-grid-margin max-w-[1200px] mx-auto ">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div className="space-y-2">
              <span className="font-label-sm text-label-sm text-golden-latte uppercase tracking-widest">
                {t("hero.freshPerspectives")}
              </span>
              <h2 className="font-headline-lg text-headline-lg text-deep-chocolate">{t("hero.newArrivals")}</h2>
            </div>
            <Link
              to="/shop"
              className="font-label-md text-label-md border-b border-deep-chocolate pb-1 hover:text-golden-latte hover:border-golden-latte transition-all self-start sm:self-auto"
            >
              {t("common.viewAll")}
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-gutter gap-y-10 items-stretch">
            {arrivals?.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Brand ethos — stacked on mobile + tablet, split from lg up */}
      <section className="bg-background py-section-gap-desktop">
        <div className="max-w-[1200px] mx-auto px-grid-margin grid lg:grid-cols-2 items-center gap-10 lg:gap-16">
          <Reveal y={32} className="w-full">
            <div className="relative w-full max-w-[450px] mx-auto lg:mx-0 overflow-hidden rounded-lg shadow-sm aspect-square">
              <img
                src={settings.ethos_image ? imgUrl(settings.ethos_image) : '/ethos.png'}
                alt={t("hero.ethosTitleA")}
                loading="lazy"
                className="w-full h-full object-cover object-center block"
              />
            </div>
          </Reveal>
          <Reveal y={32} delay={0.1} className="space-y-6 lg:space-y-8">
            <span className="font-label-sm text-label-sm text-golden-latte uppercase tracking-[0.4em]">
              {t("hero.philosophy")}
            </span>
            <h2 className="font-display-lg text-[clamp(30px,5vw,56px)] leading-[1.08] tracking-[-0.02em] text-deep-chocolate text-balance">
              {t("hero.ethosTitleA")} {t("hero.ethosTitleB")}
            </h2>
            <p className="font-body-lg text-body-lg text-on-surface-variant leading-relaxed">
              {t("hero.ethosBody")}
            </p>
          </Reveal>
        </div>
      </section>




      <ContactSection />
    </SiteLayout>
  );
}

export default HomePage;
